import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { Calendar, CaretDown, CaretRight, Plus } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SkupremeModal } from '../../components';
import { DatepickerContainer } from '../../elements/Datepicker';
import { CustomerSearchInput } from './CustomerSearch';
import { BillingService, CommonUtility, DateFormat, ToastMessage } from '../../utility';
import {
  CalenderInput,
  HoverBorderButton,
  MuteBorder,
  MuteText,
  StyledButton,
} from '../../elements';
import { InvoiceQuickBookConfigHook, useDebouncedEffect } from '../../hooks';
import { ExportInvoicesTab } from './ExportInvoicesTab';
import { CustomerPills, fieldTypes, readyPayload } from './ExportInvoiceElements/common';

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

export function ExportInvoicesPopup({ open, onClose }) {
  const {
    data: quickBookConfig,
    loading: quickBookConfigLoading,
    setData: quickBookSetData,
  } = InvoiceQuickBookConfigHook();

  // fixed fields
  const [customers, setCustomers] = useState([]);
  const [formValues, setFormValues] = useState({
    date: '',
    prefix: '',
  });

  // dynamic fields
  const [customerFormValue, setCustomerFormValue] = useState({});
  const [serviceFields, setServiceFields] = useState({});
  const [packagingMaterialFormValue, setPackagingMaterialFormValue] = useState({});

  const [updatingConfig, setUpdatingConfig] = useState(false);
  const [expand, setExpand] = useState(true);
  const [showEditView, setShowEditView] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitAfterUpdate, setSubmitAfterUpdate] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const triggerFieldChange = () => {
    setRefreshKey(Math.random());
  };

  useDebouncedEffect(
    () => {
      // it means no manual triggers
      if (refreshKey === 0) return;

      updateQuickBookConfig();
    },
    [refreshKey],
    1000,
  );

  useEffect(() => {
    // it means no manual triggers
    if (refreshKey === 0) return;

    setUpdatingConfig(true);
  }, [refreshKey]);

  const updateQuickBookConfig = useCallback(async () => {
    try {
      const fieldsPayload = readyPayload(
        serviceFields,
        quickBookConfig,
        formValues,
        packagingMaterialFormValue,
        customerFormValue,
      );

      if (fieldsPayload) {
        setUpdatingConfig(true);
        const res = await BillingService.updateQuickBookConfig(fieldsPayload);
        quickBookSetData(prevData => ({ ...prevData, ...res }));
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setUpdatingConfig(false);
    }
  }, [
    serviceFields,
    quickBookConfig,
    formValues,
    packagingMaterialFormValue,
    customerFormValue,
  ]);

  const parseServiceFormValue = (serviceMappings, currentFields) => {
    const finalValue = serviceMappings || {};

    // specifically done for fields under edit
    Object.keys(currentFields).forEach(serviceKey => {
      if (!finalValue[serviceKey]) {
        finalValue[serviceKey] = currentFields[serviceKey];
      }
    });

    return finalValue;
  };

  const parsePackageMaterialMappings = (
    packageMaterialMappings,
    packageMaterialData,
    currentFields,
  ) => {
    const packagingMaterialFormValue = {};
    Object.keys(packageMaterialMappings).forEach(materialId => {
      packagingMaterialFormValue[materialId] = {
        ...packageMaterialMappings[materialId],
        name: packageMaterialData?.find(material => material?._id === materialId)?.name,
      };
    });

    // specifically done for fields under edit
    Object.keys(currentFields).forEach(materialId => {
      if (
        !packagingMaterialFormValue[materialId] &&
        CommonUtility.isValidObject(currentFields[materialId])
      ) {
        packagingMaterialFormValue[materialId] = currentFields[materialId];
      }
    });

    return packagingMaterialFormValue;
  };

  const parseCustomerMappings = (customerMappings, customerData, currentFields) => {
    const customerFormValue = {};
    Object.keys(customerMappings).forEach(customerId => {
      customerFormValue[customerId] = {
        value: customerMappings[customerId],
        name: customerData?.find(customer => customer._id === customerId)?.name,
      };
    });

    // specifically done for fields under edit
    Object.keys(currentFields).forEach(customerId => {
      if (
        !customerFormValue[customerId] &&
        CommonUtility.isValidObject(currentFields[customerId])
      ) {
        customerFormValue[customerId] = currentFields[customerId];
      }
    });

    return customerFormValue;
  };

  useEffect(() => {
    if (!CommonUtility.isValidObject(quickBookConfig)) return;

    onFormChange('prefix', quickBookConfig?.invoiceIdPrefix);

    setServiceFields(serviceFields =>
      parseServiceFormValue(quickBookConfig?.serviceMappings, serviceFields),
    );

    setPackagingMaterialFormValue(packagingMaterialFormValue =>
      parsePackageMaterialMappings(
        quickBookConfig?.packageMaterialMappings,
        quickBookConfig?.packageMaterialData,
        packagingMaterialFormValue,
      ),
    );

    setCustomerFormValue(customerFormValue =>
      parseCustomerMappings(
        quickBookConfig?.customerMappings,
        quickBookConfig?.customerData,
        customerFormValue,
      ),
    );
  }, [quickBookConfig]);

  const onQuickBookFieldChange = (key, value) => {
    setServiceFields(fields => ({
      ...fields,
      [key]: value,
    }));
  };

  const onFormChange = (key, value) => {
    setFormValues(fields => ({
      ...fields,
      [key]: value,
    }));
  };

  const onDateChange = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formatted = `${year}-${month}-${day}`;
    onFormChange('date', formatted);
  };

  const currentDate = useMemo(() => {
    if (!formValues.date) return null;

    const [y, m, d] = formValues.date.split('-');
    return new Date(y, m - 1, d);
  }, [formValues?.date]);

  const showConfig = formValues.date;

  const onSelectCustomer = customer => {
    if (!CommonUtility.isValidObject(customer)) return;

    const alreadyExists = customers?.find(
      x => x.customerOrganizationId === customer.customerOrganizationId,
    );
    if (!alreadyExists) {
      setCustomers(customers => [...customers, customer]);
    }
  };

  const onRemoveCustomer = customer => {
    setCustomers(customers =>
      customers?.filter(
        x => x.customerOrganizationId !== customer.customerOrganizationId,
      ),
    );
  };

  const resetFields = () => {
    setRefreshKey(0);
    setCustomers([]);
    onFormChange('date', '');
    setExpand(true);
  };

  useEffect(() => {
    if (!open) {
      resetFields();
    }
  }, [open]);

  const initiateSubmission = () => {
    if (updatingConfig) {
      // set a flag that we want to submit after updating finishes
      setSubmitAfterUpdate(true);
    } else {
      onSubmit();
    }
  };

  useEffect(() => {
    if (!updatingConfig && submitAfterUpdate) {
      onSubmit();
      setSubmitAfterUpdate(false);
    }
  }, [updatingConfig, submitAfterUpdate]);

  const onSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        invoiceDate: formValues.date,
        customerOrganizationIds: customers?.map(
          customer => customer.customerOrganizationId,
        ),
      };

      const csv = await BillingService.exportQuickBook(payload);
      CommonUtility.downloadCSV(csv, `Invoice-list-${formValues.date}`);
      ToastMessage.success('Customer Invoices exported successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
      setUpdatingConfig(false);
    }
  };

  const addField = keyName => {
    setServiceFields(fields => ({
      ...(fields || {}),
      [keyName]: '',
    }));
  };

  const checkShouldDelete = (fieldToDelete, fieldType) => {
    if (fieldType === fieldTypes.service) {
      return Object.keys(quickBookConfig?.serviceMappings).includes(fieldToDelete);
    }

    if (fieldType === fieldTypes.customers) {
      return Object.keys(quickBookConfig?.customerMappings).includes(fieldToDelete);
    }

    return Object.keys(quickBookConfig?.packageMaterialMappings).includes(fieldToDelete);
  };

  const serverFieldMap = {
    [fieldTypes.customers]: 'customerMappings',
    [fieldTypes.service]: 'serviceMappings',
    [fieldTypes.packages]: 'packageMaterialMappings',
    [fieldTypes.invoiceId]: 'invoiceIdPrefix',
  };

  const deleteKeyFromDB = async (fieldToDelete, fieldType) => {
    let success = true;
    try {
      setDeleting(true);
      const params = {
        field: serverFieldMap[fieldType],
      };

      if (fieldType === fieldTypes.customers) {
        params.customerMapping = fieldToDelete;
      } else if (fieldType === fieldTypes.service) {
        params.serviceMapping = fieldToDelete;
      } else if (fieldType === fieldTypes.packages) {
        params.packageMaterialMapping = fieldToDelete;
      }

      const res = await BillingService.deleteQuickBookConfig(params);

      // remove deleted field from current formValues
      if (fieldType === fieldTypes.customers) {
        setCustomerFormValue(formValues => {
          delete formValues[fieldToDelete];
          return formValues;
        });
      } else if (fieldType === fieldTypes.service) {
        setServiceFields(serviceFields => {
          delete serviceFields[fieldToDelete];
          return serviceFields;
        });
      } else if (fieldType === fieldTypes.packages) {
        setPackagingMaterialFormValue(packagingMaterialFormValue => {
          delete packagingMaterialFormValue[fieldToDelete];
          return packagingMaterialFormValue;
        });
      }

      quickBookSetData(prevData => ({ ...prevData, ...res }));
    } catch (error) {
      success = false;
      ToastMessage.apiError(error);
    } finally {
      setDeleting(false);
    }

    return success;
  };

  const removeField = async (fieldToDelete, fieldType) => {
    const shouldDelete = checkShouldDelete(fieldToDelete, fieldType);
    // delete from DB
    if (shouldDelete) {
      await deleteKeyFromDB(fieldToDelete, fieldType);
      return;
    }
    // only update state on Frontend
    if (fieldType === fieldTypes.service) {
      const newObject = {};
      Object.keys(serviceFields || {}).forEach(quickKey => {
        if (quickKey !== fieldToDelete) {
          newObject[quickKey] = serviceFields[quickKey];
        }
      });
      setServiceFields(newObject);
    } else if (fieldType === fieldTypes.customers) {
      const newObject = {};
      Object.keys(customerFormValue || {}).forEach(customerId => {
        if (customerId !== fieldToDelete) {
          newObject[customerId] = customerFormValue[customerId];
        }
      });
      setCustomerFormValue(newObject);
    } else if (fieldType === fieldTypes.packages) {
      const newObject = {};
      Object.keys(packagingMaterialFormValue || {}).forEach(packageId => {
        if (packageId !== fieldToDelete) {
          newObject[packageId] = packagingMaterialFormValue[packageId];
        }
      });
      setPackagingMaterialFormValue(newObject);
    }
  };

  const onEdit = () => {
    setShowEditView(true);
  };

  return (
    <SkupremeModal size="small" open={open} onClose={onClose} title="Export Invoice">
      {showConfig ? (
        <div
          onClick={() => setExpand(!expand)}
          className={`d-flex align-items-center cursor-pointer justify-content-between ${
            expand ? '' : 'mb-2'
          }`}
        >
          <b className="mr-1">Data Source</b>
          {expand ? (
            <CaretDown size={16} weight="bold" />
          ) : (
            <CaretRight size={16} weight="bold" />
          )}
        </div>
      ) : (
        <b className="mr-1">Data Source</b>
      )}
      {expand && (
        <div className="mb-2 mt-2">
          <Field>
            <MuteText>Customer</MuteText>
            <div className="d-flex w-100">
              <CustomerSearchInput
                onResultClick={onSelectCustomer}
                inputClassName="w-100 similar-height"
                clearInput
              />
              {CommonUtility.isValidArray(customers) && (
                <HoverBorderButton
                  onClick={() => setCustomers([])}
                  className="flex-btn text-nowrap ml-2"
                >
                  Select All
                </HoverBorderButton>
              )}
            </div>
            <CustomerPills customers={customers} onRemoveCustomer={onRemoveCustomer} />
          </Field>
          <Field className="mt-2">
            <MuteText>Invoice Date</MuteText>
            <DatepickerContainer>
              <DatePicker
                className="w-100"
                formatWeekDay={nameOfDay => nameOfDay.substring(0, 3)}
                selected={currentDate}
                onChange={date => {
                  if (date) {
                    onDateChange(date);
                  } else {
                    onFormChange('date', '');
                  }
                }}
                placeholderText="Select Date"
                customInput={
                  <CalenderInput
                    className="similar-height"
                    icon={<Calendar className="icon" size={26} />}
                  />
                }
                dateFormat={DateFormat.datepicker}
                disabledKeyboardNavigation
                maxDate={new Date()}
              />
            </DatepickerContainer>
          </Field>
        </div>
      )}
      {!expand && <MuteBorder className="mb-2" />}
      {showConfig && (
        <ExportInvoicesTab
          expand={expand}
          setExpand={setExpand}
          quickBookConfigLoading={quickBookConfigLoading}
          formValues={formValues}
          onFormChange={onFormChange}
          serviceFields={serviceFields}
          onQuickBookFieldChange={onQuickBookFieldChange}
          removeField={removeField}
          quickBookConfig={quickBookConfig}
          addField={addField}
          showEditView={showEditView}
          setShowEditView={setShowEditView}
          packagingMaterialFormValue={packagingMaterialFormValue}
          setPackagingMaterialFormValue={setPackagingMaterialFormValue}
          customerFormValue={customerFormValue}
          setCustomerFormValue={setCustomerFormValue}
          deleting={deleting}
          updatingConfig={updatingConfig}
          triggerFieldChange={triggerFieldChange}
          showLoader={refreshKey !== 0}
        />
      )}
      <div className="d-flex justify-content-between mt-4">
        <div>
          {!showEditView && showConfig && (
            <HoverBorderButton onClick={onEdit} className="flex-btn">
              <Plus className="mr-1" /> Configuration
            </HoverBorderButton>
          )}
        </div>
        <div className="d-flex">
          <HoverBorderButton onClick={onClose} disabled={loading}>
            Cancel
          </HoverBorderButton>
          <StyledButton
            onClick={initiateSubmission}
            disabled={!showConfig}
            className="ml-2"
            loading={loading || submitAfterUpdate}
          >
            Confirm
          </StyledButton>
        </div>
      </div>
    </SkupremeModal>
  );
}
